import type { FieldValues, Path, Control } from 'react-hook-form'

import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from 'src/shared/components/ui/forms/form'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/shared/components/ui/select'
import type { MapItem } from 'src/shared/schemas/utils'
import { cn } from 'src/shared/utils/cn'

interface SelectFormFieldProps<T extends FieldValues> {
  control?: Control<T>
  name: Path<T>
  label: string
  options: Record<string, MapItem>
  description?: string
  className?: string
  fieldClassName?: string
  disabled?: boolean
  required?: boolean
  onValueChange?: (value: string) => void
}

export function SelectFormField<T extends FieldValues>({
  control,
  name,
  label,
  options,
  description,
  className,
  fieldClassName,
  disabled,
  required = false,
  onValueChange,
}: SelectFormFieldProps<T>) {
  if (required) {
    label = `${label} *`
  }

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn('w-full', className)}>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <div className={cn('relative', fieldClassName)}>
              <Select
                onValueChange={(value) => {
                  field.onChange(value)
                  onValueChange?.(value)
                }}
                value={field.value}
                disabled={disabled}
              >
                <SelectTrigger className="bg-accent">
                  <SelectValue placeholder={'Select'} />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(options).map(([value, { label }]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  )
}
